import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const redirect = searchParams.get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success(t('auth.login_success'));
      navigate(redirect);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.login_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8F4FD] flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex">
            <img src="/assets/padsmash_logo_main.png" alt="PadSmash" className="h-36 sm:h-44 w-auto mx-auto" />
          </Link>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#D6E8F0]">
          <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">{t('auth.login_title')}</h1>
          <p className="text-[#5A8BA8] mb-6">{t('auth.login_subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.ph_email')}
                className="mt-2"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A8BA8]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? t('auth.login_loading') : t('auth.login_btn')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <p className="text-center text-sm text-[#5A8BA8] mt-6">
            {t('auth.no_account')}{' '}
            <Link to="/register" className="text-[#0C63BF] font-medium hover:underline">
              {t('auth.signup_link')}
            </Link>
          </p>
          <p className="text-center text-sm mt-2">
            <Link to="/forgot-password" className="text-[#5A8BA8] hover:text-[#0C63BF] text-xs hover:underline" data-testid="forgot-password-link">
              {t('auth.forgot_password')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });

  const redirect = searchParams.get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData);
      toast.success(t('auth.register_success'));
      navigate('/verify-email');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.register_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8F4FD] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex">
            <img src="/assets/padsmash_logo_main.png" alt="PadSmash" className="h-36 sm:h-44 w-auto mx-auto" />
          </Link>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#D6E8F0]">
          <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">{t('auth.register_title')}</h1>
          <p className="text-[#5A8BA8] mb-6">{t('auth.register_subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">{t('auth.first_name')}</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder={t('auth.ph_first_name')}
                  className="mt-2"
                  required
                  data-testid="register-firstname"
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t('auth.last_name')}</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder={t('auth.ph_last_name')}
                  className="mt-2"
                  required
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.ph_email')}
                className="mt-2"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="phone">{t('auth.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('auth.ph_phone')}
                className="mt-2"
                required
                data-testid="register-phone"
              />
            </div>

            <div className="border-t border-[#E8F4FD] pt-4 mt-2">
              <p className="text-sm font-semibold text-[#033871] mb-3">{t('auth.pickup_address')}</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="address">{t('auth.address')}</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={t('auth.ph_address')}
                    className="mt-2"
                    required
                    data-testid="register-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">{t('auth.postal_code')}</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder={t('auth.ph_postal')}
                      className="mt-2"
                      required
                      data-testid="register-postal-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">{t('auth.city')}</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder={t('auth.ph_city')}
                      className="mt-2"
                      required
                      data-testid="register-city"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.password_placeholder')}
                  required
                  minLength={8}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A8BA8]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.password && (() => {
                const p = formData.password;
                let score = 0;
                if (p.length >= 8) score++;
                if (/[A-Z]/.test(p)) score++;
                if (/[a-z]/.test(p)) score++;
                if (/\d/.test(p)) score++;
                if (/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(p)) score++;
                const labels = ['', t('auth.pw_very_weak'), t('auth.pw_weak'), t('auth.pw_medium'), t('auth.pw_strong'), t('auth.pw_very_strong')];
                const colors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-[#32CD32]', 'bg-[#32CD32]'];
                return (
                  <div className="mt-2 space-y-1.5" data-testid="register-password-strength">
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= score ? colors[score] : 'bg-gray-200'}`} />
                      ))}
                    </div>
                    <p className={`text-xs font-medium ${score >= 4 ? 'text-[#32CD32]' : score >= 3 ? 'text-yellow-600' : 'text-red-500'}`}>{labels[score]}</p>
                    <ul className="text-[10px] text-[#5A8BA8] space-y-0.5">
                      <li className={p.length >= 8 ? 'text-[#32CD32]' : ''}>{p.length >= 8 ? '✓' : '○'} {t('auth.pw_min_chars')}</li>
                      <li className={/[A-Z]/.test(p) ? 'text-[#32CD32]' : ''}>{/[A-Z]/.test(p) ? '✓' : '○'} {t('auth.pw_uppercase')}</li>
                      <li className={/\d/.test(p) ? 'text-[#32CD32]' : ''}>{/\d/.test(p) ? '✓' : '○'} {t('auth.pw_digit')}</li>
                      <li className={/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(p) ? 'text-[#32CD32]' : ''}>{/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(p) ? '✓' : '○'} {t('auth.pw_special')}</li>
                    </ul>
                  </div>
                );
              })()}
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? t('auth.register_loading') : t('auth.register_btn')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <p className="text-center text-sm text-[#5A8BA8] mt-6">
            {t('auth.have_account')}{' '}
            <Link to="/login" className="text-[#0C63BF] font-medium hover:underline">
              {t('auth.login_link')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
